import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useTranslation } from "react-i18next"
import { cursorAtom } from "@/store/brush-cursor-state"
import { currentAdjustmentsAtom, saveCurrentAdjustmentState } from "@/store/store-history"
import { Slider } from "@/components/ui/Slider"
import {
	healingAiTemporaryItemAtom,
	healingAiPendingAtom,
	healingAiPromptDerivedAtom,
	healingModeAtom,
	healingAiShowMaskAtom,
	healingAiReferenceImageAtom,
	healingApplyBrushMaskAtom,
	healingApplyAlignmentAtom,
	isHealingPreviewEnabledAtom,
	resetAllHealings,
	selectedHealingAtom,
	updateHealingItem,
	generateAiHealing,
	clearAiHealingCandidate,
	cancelHealingAiGeneration,
	removeHealingItem,
	healingAiVisibleItemAtom,
} from "@/store/editor/healing-state"
import { currentPhotoAtom } from "@/store/photo/photos-state"
import { healingSizeToSliderValue, sliderValueToHealingSize } from "@/core/utils/brushing"
import { Icon } from "../icons/Icon"
import { useEditor } from "@/utils/useEditor"
import { Button } from "../ui/Button"
import {
	healingParametersAdjustingAtom,
	mouseOverSelectedHealingPanelAtom,
	showAdjustingBrushCursorAtom,
	showingHealingPanelAtom,
} from "@/store/ui-views-state"
import { pushDialog } from "@/store/dialogs-state"
import { useEffect, useRef } from "react"
import clsx from "clsx"
import { isAIDebugEnabled, isGenerativeFillEnabled } from "@/utils/measurement/feature-flag"
import { Switch } from "../ui/Switch"
import { useShortcuts } from "@/utils/useShortcuts"
import { HealingMode } from "@/core/types"
import { forceRefreshUser, useUserQuery } from "@/api/users-api"
import { hasUserActiveSubscription } from "@/api/user-helpers"
import {
	HealingAiProgressDialog,
	HealingAiProgressMode,
} from "@/components/Dialogs/HealingAiProgressDialog"
import { actionShortcuts, eventToActionKey } from "@/shortcuts/shortcuts"
import { openBasicEditView } from "@/store/photo/photos-view-state"
import {
	SECRET_SURPRISE_BUTTON_CLASS,
	isSecretSurpriseUnlocked,
	openSecretSurpriseDialog,
} from "./healing-surprise"
import { ContentTooltipWrapper } from "../ui/ContentTooltip"
import {
	MAX_AI_HEALING_VARIATIONS,
	canAddAiHealingVariationBatch,
} from "@/store/editor/healing-variation-stack"

export const HealingPanel = () => {
	const handleUpdateEnded = (name: string, val?: number | string) => {
		saveCurrentAdjustmentState({
			message: `Remove: ${name}`,
			changedValue: val?.toString(),
		})
	}

	return <HealingSettings onUpdateEnded={handleUpdateEnded} />
}

interface HealingSettingsProps {
	onUpdateEnded: (name: string, val?: number | string) => void
}

const HealingSettings = (props: HealingSettingsProps) => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const [cursor, setCursor] = useAtom(cursorAtom)
	const [isHealingPreviewEnabled, setHealingPreviewEnabled] = useAtom(isHealingPreviewEnabledAtom)
	const adjustments = useAtomValue(currentAdjustmentsAtom)
	const [selectedHealing, setSelectedHealing] = useAtom(selectedHealingAtom)
	const [isHealingActive, setHealingActive] = useAtom(showingHealingPanelAtom)
	const [healingMode, setHealingMode] = useAtom(healingModeAtom)
	const [aiPrompt, setAiPrompt] = useAtom(healingAiPromptDerivedAtom)
	const [showMask, setShowMask] = useAtom(healingAiShowMaskAtom)
	const [referenceImages, setReferenceImages] = useAtom(healingAiReferenceImageAtom)
	const isAiPending = useAtomValue(healingAiPendingAtom)
	const [applyBrushMask, setApplyBrushMask] = useAtom(healingApplyBrushMaskAtom)
	const [applyAlignment, setApplyAlignment] = useAtom(healingApplyAlignmentAtom)
	const generativeFillEnabled = isGenerativeFillEnabled()
	const userQuery = useUserQuery()
	const user = userQuery.data
	const genAiCreditsRemaining = user?.totalGenAiCreditsAvailable
	const hasKnownGenAiCredits = typeof genAiCreditsRemaining === "number"
	const isCreditsLoading = !hasKnownGenAiCredits && (userQuery.isLoading || userQuery.isFetching)
	// Keep actions enabled when credits are unknown to avoid blocking users during query load/failure.
	// Backend credit consumption check is authoritative and will reject if credits are actually exhausted.
	const isOutOfGenAiCredits = hasKnownGenAiCredits && genAiCreditsRemaining <= 0
	const temporaryItem = useAtomValue(healingAiTemporaryItemAtom)
	const visibleItem = useAtomValue(healingAiVisibleItemAtom)

	const currentPhoto = useAtomValue(currentPhotoAtom)

	const getGenAiCreditsStatusLabel = () => {
		if (hasKnownGenAiCredits) {
			return t("healingPanel.creditsRemaining", {
				count: genAiCreditsRemaining,
			})
		}

		if (isCreditsLoading) {
			return t("healingPanel.loadingCredits")
		}

		return t("healingPanel.creditsUnavailable")
	}

	// AI Generate requires a non-empty prompt; Erase mode does not (server writes its own).
	const isPromptRequiredButEmpty = healingMode === "ai-generate" && !aiPrompt.trim()
	const isAtAiVariationLimit =
		!!selectedHealing?.aiVariations?.length &&
		!canAddAiHealingVariationBatch(selectedHealing.aiVariations.length)

	const getAiActionTooltip = () => {
		if (isSecretPromptUnlocked) {
			return undefined
		}

		if (isOutOfGenAiCredits) {
			return t("healingPanel.outOfCredits")
		}

		if (isAtAiVariationLimit) {
			return t("healingPanel.variationLimitReached", { max: MAX_AI_HEALING_VARIATIONS })
		}

		if (!temporaryItem && !selectedHealing?.aiVariations && !isAiPending) {
			return t("healingPanel.pleaseBrushFirst")
		}

		if (isPromptRequiredButEmpty && !isAiPending) {
			return t("healingPanel.pleaseEnterPrompt")
		}

		return undefined
	}

	const genAiCreditsStatusLabel = getGenAiCreditsStatusLabel()
	const genAiCreditsTooltip = hasUserActiveSubscription(user)
		? t("healingPanel.subscriberCreditsTooltip")
		: t("healingPanel.freeUserCreditsTooltip")

	// Ref for the prompt textarea to manage focus
	const promptTextareaRef = useRef<HTMLTextAreaElement>(null)
	// Ref for the file input to add reference images
	const referenceImageInputRef = useRef<HTMLInputElement>(null)

	// Clean up temporary healing item, selection, prompt, references when photo changes or panel closes
	useEffect(() => {
		// Clear on mount (no-op if already null) and whenever photo changes
		clearAiHealingCandidate()
		setSelectedHealing(null)
		setAiPrompt("")
		setReferenceImages([])

		// Cleanup on unmount (panel closed)
		return () => {
			clearAiHealingCandidate()
			setSelectedHealing(null)
			setAiPrompt("")
			setReferenceImages([])
		}
	}, [currentPhoto?.id, setSelectedHealing, setAiPrompt, setReferenceImages])

	useEffect(() => {
		if (generativeFillEnabled) {
			void forceRefreshUser()
		}
	}, [generativeFillEnabled])

	// Blur prompt input when any healing item is selected
	useEffect(() => {
		if (selectedHealing) {
			promptTextareaRef.current?.blur()
		}
	}, [selectedHealing])

	useEffect(() => {
		const handleClickOutside = (e: any) => {
			if (
				promptTextareaRef.current &&
				!promptTextareaRef.current.contains(e.target) &&
				document.activeElement === promptTextareaRef.current
			) {
				promptTextareaRef.current.blur()
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () => document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	const switchModeManually = (mode: HealingMode) => {
		setHealingMode(mode)
		// Always clear temporary items when mode switches
		clearAiHealingCandidate()
		// Reset the selection
		setSelectedHealing(null)
	}

	// When a healing item is selected: switch mode if needed, then update UI
	useEffect(() => {
		if (selectedHealing) {
			// First, switch to the healing item's mode if different
			const healingItemMode = selectedHealing.mode ?? "healing"
			if (healingItemMode !== healingMode) {
				setHealingMode(healingItemMode)
				clearAiHealingCandidate()
			}

			// Then, update UI based on the selected healing
			if (selectedHealing.aiVariations?.length) {
				// AI healing selected -> show stored prompt
				const selectedVariation = selectedHealing.selectedVariation ?? 0
				const variation = selectedHealing.aiVariations?.[selectedVariation]
				if (healingItemMode === "ai-generate" || healingItemMode === "ai-erase") {
					const prompt = variation?.prompt ?? ""
					setAiPrompt(prompt)
				}

				// Sync checkbox with selected healing's brush mask setting
				const healingApplyBrushMask = selectedHealing.applyBrushMask ?? true
				setApplyBrushMask(healingApplyBrushMask)

				// Clear temporary item if we selected a permanent healing
				if (temporaryItem) {
					clearAiHealingCandidate()
				}
			}
		} else {
			// No selection -> reset to default brush mask state
			setApplyBrushMask(true)
		}
	}, [
		selectedHealing,
		healingMode,
		temporaryItem,
		setHealingMode,
		setAiPrompt,
		setApplyBrushMask,
	])

	// Handle brush mask toggle for existing healings
	const handleApplyBrushMaskChange = (checked: boolean) => {
		setApplyBrushMask(checked)

		// If there's a selected healing, update it immediately
		if (selectedHealing) {
			updateHealingItem(selectedHealing.id, {
				healingProps: {
					applyBrushMask: checked,
				},
			})

			saveCurrentAdjustmentState({
				message: `Remove`,
				changedValue: checked ? "Use brush mask" : "Expand to rectangle mask",
			})
		}
	}

	// Handle alignment toggle for existing healings
	const handleApplyAlignmentChange = (checked: boolean) => {
		setApplyAlignment(checked)

		// If there's a selected healing, update it immediately
		if (selectedHealing) {
			updateHealingItem(selectedHealing.id, {
				healingProps: {
					applyAlignment: checked,
				},
			})

			saveCurrentAdjustmentState({
				message: `Remove`,
				changedValue: checked ? "Enable alignment" : "Disable alignment",
			})
		}
	}

	// Manage reference images when showMask toggle changes
	useEffect(() => {
		if (showMask && referenceImages.length > 1) {
			// If turning on showMask and have 2+ references, keep only the first one
			setReferenceImages((prev) => prev.slice(0, 1))
		}
	}, [showMask, referenceImages.length, setReferenceImages])

	const handleGenerateAi = () => {
		if (isSecretPromptUnlocked) {
			openSecretSurpriseDialog()
			return
		}

		const mode: HealingAiProgressMode = healingMode === "ai-erase" ? "ai-erase" : "ai-generate"

		pushDialog(HealingAiProgressDialog, {
			mode,
			onStart: generateAiHealing,
			onCancel: cancelHealingAiGeneration,
		})
	}

	// Handle Enter key in prompt textarea
	const handlePromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter") {
			// Shift+Enter = allow newline
			if (e.shiftKey) {
				return // Allow default behavior for newline
			}

			// Plain Enter = generate/regenerate
			e.preventDefault()
			promptTextareaRef.current?.blur()

			// Only generate if button would be enabled
			if (!canRunAiAction) {
				return
			}

			handleGenerateAi()
		} else if (e.key === "Escape") {
			e.preventDefault()
			promptTextareaRef.current?.blur()
		}
	}
	const isAiMode = healingMode === "ai-generate" || healingMode === "ai-erase"
	const hasMaskDraft = !!temporaryItem
	const isAiHealingItem = selectedHealing?.aiVariations !== undefined
	const hasAiGenerationSource = !!temporaryItem || !!selectedHealing?.aiVariations
	const isSecretPromptUnlocked = isSecretSurpriseUnlocked(healingMode, aiPrompt)
	const canRunAiAction =
		(hasAiGenerationSource || isSecretPromptUnlocked) &&
		!isAiPending &&
		(!isOutOfGenAiCredits || isSecretPromptUnlocked) &&
		(!isAtAiVariationLimit || isSecretPromptUnlocked) &&
		(!isPromptRequiredButEmpty || isSecretPromptUnlocked)
	const isPersistedSelectedHealing =
		selectedHealing !== null &&
		(adjustments.healingMasks?.some((healing) => healing.id === selectedHealing.id) ?? false)
	const shouldShowSelectedHealingSection = isPersistedSelectedHealing || !!visibleItem
	const setMouseOverSelectedHealingPanel = useSetAtom(mouseOverSelectedHealingPanelAtom)
	const setIsHealingParametersAdjusting = useSetAtom(healingParametersAdjustingAtom)
	const setShowAdjustingBrushCursor = useSetAtom(showAdjustingBrushCursorAtom)
	const selectedVariationIndex = selectedHealing?.selectedVariation ?? 0
	const aiVariationCount = selectedHealing?.aiVariations?.length ?? 0
	const shouldShowAiVariationControls = isAiMode && aiVariationCount > 1
	const showAiVariationThumbnails = shouldShowAiVariationControls && isAIDebugEnabled()

	useEffect(() => {
		return () => {
			setMouseOverSelectedHealingPanel(false)
			setIsHealingParametersAdjusting(false)
			setShowAdjustingBrushCursor(false)
		}
	}, [
		setMouseOverSelectedHealingPanel,
		setIsHealingParametersAdjusting,
		setShowAdjustingBrushCursor,
	])

	// Keyboard shortcut: Enter to generate/erase (only when not in textarea)
	useShortcuts({
		keys: [actionShortcuts.enter, actionShortcuts.escape],
		action: (event) => {
			if (isAiMode) {
				if (
					eventToActionKey(event) === actionShortcuts.enter &&
					isAiMode &&
					hasMaskDraft &&
					isAiHealingItem &&
					!isOutOfGenAiCredits &&
					!isPromptRequiredButEmpty
				) {
					handleGenerateAi()
					return
				}

				if (hasMaskDraft || selectedHealing) {
					setSelectedHealing(null)
					clearAiHealingCandidate()
				} else {
					// exists healing
					openBasicEditView()
				}
			} else {
				if (selectedHealing) {
					setSelectedHealing(null)
				} else if (isHealingActive) {
					setHealingActive(false)
				}
			}
		},
		trackingLabel: "healing_enter_escape",
		trackingValue: (event) => {
			if (eventToActionKey(event) === actionShortcuts.enter) {
				if (healingMode === "ai-erase") return "enter_ai_erase"
				if (healingMode === "ai-generate") return "enter_ai_generate"
				return selectedHealing ? "enter_unselect_healing" : "enter_cancel_healing"
			}

			if (healingMode === "ai-erase") return "escape_ai_erase"
			if (healingMode === "ai-generate") return "escape_ai_generate"
			return selectedHealing ? "escape_unselect_healing" : "escape_cancel_healing"
		},
		disabled: isAiPending,
		preventDefault: true,
	})

	const handleReferenceImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file && file.type.startsWith("image/")) {
			setReferenceImages((prev) => [...prev, file])
		}
		// Reset the input so the same file can be selected again if needed
		event.target.value = ""
	}

	const handleAddReferenceClick = () => {
		referenceImageInputRef.current?.click()
	}

	const handleRemoveReferenceImage = (index: number) => {
		setReferenceImages((prev) => prev.filter((_, i) => i !== index))
	}

	const handleVariationSelect = (variationIndex: number) => {
		if (!selectedHealing?.aiVariations) return

		const variation = selectedHealing.aiVariations[variationIndex]
		if (!variation) return

		const historyChangedValue = `Select Variant ${variationIndex + 1}`
		const aiMode = selectedHealing.mode
		if (aiMode !== "ai-erase" && aiMode !== "ai-generate") return

		// Update the healing item
		updateHealingItem(selectedHealing.id, {
			healingProps: {
				selectedVariation: variationIndex,
			},
		})

		const variationModeLabel = aiMode === "ai-erase" ? "AI Erase" : "AI Generate"

		saveCurrentAdjustmentState({
			message: variationModeLabel,
			changedValue: historyChangedValue,
			trackingProperties: {
				historyChangedValue,
				aiMode,
				aiVariantIndex: variationIndex + 1,
				aiVariationCount: selectedHealing.aiVariations.length,
				aiVariantPromptStrategy: variation.promptStrategy ?? "unknown",
			},
		})
	}

	const handleVariationStep = (direction: -1 | 1) => {
		if (!selectedHealing?.aiVariations?.length) return

		const variationCount = selectedHealing.aiVariations.length
		const nextVariation = (selectedVariationIndex + direction + variationCount) % variationCount
		handleVariationSelect(nextVariation)
	}

	const deleteSelectedHealing = () => {
		if (!selectedHealing) return

		if (isPersistedSelectedHealing) {
			removeHealingItem(selectedHealing)
		}

		clearAiHealingCandidate()
	}

	useEffect(() => {
		setHealingPreviewEnabled(true)
	}, [])

	const onUpdateHandler = (name, val) => {
		// props.onUpdate(name, val)
		const newCursor = { ...cursor, [name]: val }
		setCursor(newCursor)
	}

	const onUpdateSelectedHealing = (name, val) => {
		if (!selectedHealing) return

		const id = selectedHealing.id
		updateHealingItem(id, { strokeProps: { [name]: val } })
	}

	const handlePointerDown = (_) => {
		setHealingPreviewEnabled(false)
		editor.enableHealing = false
	}

	const handlePointerUp = (_) => {
		setHealingPreviewEnabled(true)
		editor.enableHealing = true
	}

	const genAICreditsView = (withDivider: boolean) => {
		return (
			<div
				className={clsx(
					withDivider && "border-t pt-2",
					"mt-2 flex flex-row items-center justify-end space-x-1 border-base-700 text-right text-xxs text-base-500",
				)}
			>
				<div>{genAiCreditsStatusLabel}</div>
				<ContentTooltipWrapper
					tooltipDelay={0}
					tooltip={genAiCreditsTooltip}
					tooltipProps={{ side: "bottom", sideOffset: 8 }}
				>
					<Icon name="info" size={14} className="text-base-500" />
				</ContentTooltipWrapper>
			</div>
		)
	}
	return (
		<div aria-label={t("healingPanel.ariaHealingPanel")} className="m-0 p-2">
			{generativeFillEnabled && (
				<div className="mb-3">
					<div className="flex w-full flex-row justify-center pt-1 text-xs">
						<div
							className={clsx(
								healingMode === "ai-erase"
									? "text-base-50"
									: "text-base-500 dark:text-base-400",
								"border-r border-base-700 px-2",
								"hover:text-base-200",
								isAiPending && "pointer-events-none opacity-50",
							)}
							onClick={() => switchModeManually("ai-erase")}
						>
							{t("healingPanel.aiErase")}
						</div>
						<div
							className={clsx(
								healingMode === "ai-generate"
									? "text-base-50"
									: "text-base-500 dark:text-base-400",
								"border-r border-base-700 px-2",
								"hover:text-base-200",
								isAiPending && "pointer-events-none opacity-50",
							)}
							onClick={() => switchModeManually("ai-generate")}
						>
							{t("healingPanel.aiGenerate")}
						</div>
						<div
							className={clsx(
								healingMode === "healing"
									? "text-base-50"
									: "text-base-500 dark:text-base-400",
								"px-2",
								"hover:text-base-200",
								isAiPending && "pointer-events-none opacity-50",
							)}
							onClick={() => switchModeManually("healing")}
						>
							{t("healingPanel.heal")}
						</div>
					</div>

					{healingMode === "ai-generate" && (
						<div className="mt-3">
							{/* Prompt Section */}
							<div className="rounded-md border border-base-700 bg-base-900">
								{/* Reference Images Thumbnails - Inside prompt box */}
								{referenceImages.length > 0 && (
									<div className="flex gap-2 p-2">
										{/* User Reference Images Thumbnails */}
										{referenceImages.map((referenceImage, index) => (
											<div key={index} className="relative">
												<img
													src={URL.createObjectURL(referenceImage)}
													alt={t("healingPanel.reference", {
														var0: index + 1,
													})}
													className="size-16 rounded-md border border-base-600 object-cover"
												/>
												<button
													onClick={() =>
														handleRemoveReferenceImage(index)
													}
													className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-blue-500 text-white transition-colors hover:bg-blue-600"
													title={t("healingPanel.removeReferenceImage")}
												>
													<Icon name="delete" size={12} />
												</button>
											</div>
										))}
									</div>
								)}

								<textarea
									ref={promptTextareaRef}
									className={clsx(
										"w-full resize-none rounded-md bg-transparent p-2 text-xs text-base-100 focus:outline-none",
										referenceImages.length > 0 || showMask ? "h-24" : "h-24",
									)}
									placeholder={t("healingPanel.describePlaceholder")}
									value={aiPrompt}
									disabled={isAiPending}
									onChange={(event) => setAiPrompt(event.target.value)}
									onKeyDown={handlePromptKeyDown}
								/>
								<input
									ref={referenceImageInputRef}
									type="file"
									accept="image/*"
									className="hidden"
									onChange={handleReferenceImageUpload}
								/>
								<div className="border-base-700 p-2">
									<div className="flex items-center justify-between">
										<Button
											trackingLabel="healing-ai-add-reference"
											variant="custom"
											size="custom"
											disabled={
												isAiPending ||
												(showMask ? 1 : 0) + referenceImages.length >= 2
											}
											onClick={handleAddReferenceClick}
											className="bg-transparent p-0 hover:bg-base-700"
											tooltip={t("healingPanel.tooltipAddReference")}
										>
											<Icon name="add" size={24} />
										</Button>
										<Button
											trackingLabel="healing-ai-generate"
											variant="primary"
											size="sm"
											disabled={!canRunAiAction}
											onClick={handleGenerateAi}
											tooltip={getAiActionTooltip()}
											className={clsx(
												isSecretPromptUnlocked &&
													SECRET_SURPRISE_BUTTON_CLASS,
											)}
										>
											{isAiPending
												? t("healingPanel.generating")
												: selectedHealing?.aiVariations?.length
													? t("healingPanel.regenerate")
													: t("healingPanel.generate")}
										</Button>
									</div>

									{genAICreditsView(true)}
								</div>
							</div>

							{/* Send mask to AI checkbox - Only shown in debug mode */}
							{isAIDebugEnabled() && (
								<div className="mb-1">
									<div className="mt-2 flex items-center gap-2">
										<label className="flex cursor-pointer items-center gap-2 text-xs text-base-300">
											<input
												type="checkbox"
												checked={showMask}
												onChange={(e) => setShowMask(e.target.checked)}
												disabled={isAiPending}
												className="text-primary-500 focus:ring-primary-500 size-3 rounded border-base-600 bg-base-800 focus:ring-1"
											/>
											<span>{t("healingPanel.sendMaskToAi")}</span>
										</label>
									</div>
									<p className="mt-1 text-xxs leading-relaxed text-base-500">
										{t("healingPanel.sendMaskDescription")}
									</p>
								</div>
							)}
						</div>
					)}

					{/* Erase mode: Show Erase button */}
					{healingMode === "ai-erase" && (
						<div className="mt-3 pr-1">
							<div className="border-base-700 p-2">
								<Button
									trackingLabel="healing-ai-erase"
									variant="primary"
									disabled={
										(!temporaryItem && !selectedHealing?.aiVariations) ||
										isAiPending ||
										isOutOfGenAiCredits ||
										isAtAiVariationLimit
									}
									onClick={handleGenerateAi}
									className="w-full"
									tooltip={getAiActionTooltip()}
								>
									{isAiPending
										? t("healingPanel.erasing")
										: selectedHealing?.aiVariations?.length
											? t("healingPanel.reErase")
											: t("healingPanel.erase")}
								</Button>
								{genAICreditsView(false)}
							</div>
						</div>
					)}

					{/* AI debug only: Show the prompt the server actually used.
					    - Erase mode: the prompt used by the server is returned as `enhanced_prompt`.
					      Enhanced variants return the enhanced prompt; skip-enhancement variants return
					      the server-side default prompt. A frontend prompt may be sent, but erase mode
					      does not use it for inference.
					    - Generate mode: today the server returns the user's prompt verbatim; if generate
					      ever starts enhancing, we can surface the persisted `variation.enhancedPrompt`.
						  This won't overwrite the prompt field (which keeps the user's original input for display and regeneration). */}
					{isAIDebugEnabled() &&
						healingMode === "ai-erase" &&
						selectedHealing?.aiVariations &&
						(() => {
							const idx = selectedHealing.selectedVariation ?? 0
							const variation = selectedHealing.aiVariations[idx]
							const enhancedPrompt = variation?.enhancedPrompt
							if (!enhancedPrompt) return null
							return (
								<div className="mt-2 rounded-md border border-base-700 bg-base-900">
									{/* i18next-instrument-ignore-next-line */}
									<div className="px-2 pt-2 text-xxs font-medium tracking-wide text-base-400">
										{t("healingPanel.aiEnhancedPromptDebug")}
									</div>
									<textarea
										readOnly
										aria-label={t("healingPanel.ariaAiEnhancedPromptDebug")}
										value={enhancedPrompt}
										className="h-24 w-full resize-none rounded-md bg-transparent p-2 text-xs text-base-100 focus:outline-none"
									/>
								</div>
							)
						})()}

					{/* AI variation controls. Thumbnails are debug-only; arrows are always available. */}
					{shouldShowAiVariationControls && selectedHealing?.aiVariations && (
						<div className="mt-2 py-1">
							<div className="flex items-center gap-3 text-xs text-base-400 transition-colors hover:text-base-200">
								<div className="min-w-0 flex-1 font-medium">
									{t("healingPanel.variations")}
								</div>
								<button
									type="button"
									onClick={() => handleVariationStep(-1)}
									disabled={isAiPending}
									aria-label={t("healingPanel.ariaPreviousAiVariation")}
									className="flex size-7 items-center justify-center rounded-full transition-colors hover:text-base-100 disabled:pointer-events-none disabled:opacity-40"
								>
									<Icon name="left" size={24} />
								</button>
								<div className="w-12 text-center tabular-nums">
									{t("healingPanel.variationProgress", {
										current: selectedVariationIndex + 1,
										total: aiVariationCount,
									})}
								</div>
								<button
									type="button"
									onClick={() => handleVariationStep(1)}
									disabled={isAiPending}
									aria-label={t("healingPanel.ariaNextAiVariation")}
									className="flex size-7 items-center justify-center rounded-full transition-colors hover:text-base-100 disabled:pointer-events-none disabled:opacity-40"
								>
									<Icon name="right" size={24} />
								</button>
							</div>

							{showAiVariationThumbnails && (
								<div className="mt-2 flex gap-2">
									{selectedHealing.aiVariations.map((variation, index, arr) => {
										const isSelected = selectedVariationIndex === index
										const isSingleVariation = arr.length === 1
										return (
											<button
												key={index}
												type="button"
												onClick={() => handleVariationSelect(index)}
												disabled={isAiPending}
												className={`relative aspect-square overflow-hidden rounded-md border-2 transition-all disabled:pointer-events-none disabled:opacity-50 ${
													isSingleVariation ? "w-1/2" : "flex-1"
												} ${
													isSelected
														? "border-primary-500 shadow-lg"
														: "border-base-600 hover:border-base-500"
												} `}
												title={t("healingPanel.variationLabel", {
													index: index + 1,
												})}
											>
												<img
													src={`data:${variation.mimeType};base64,${variation.data}`}
													alt={t("healingPanel.aiVariation", {
														var0: index + 1,
													})}
													className={clsx("size-full object-cover")}
													style={{
														transform: `rotate(${
															((((variation.orientation ?? 0) % 4) +
																4) %
																4) *
															90
														}deg)`,
														transformOrigin: "50% 50%",
													}}
												/>
											</button>
										)
									})}
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{/* Brush Setting Section */}
			<div
				className="border-t border-base-900 py-1"
				onPointerEnter={() => {
					setShowAdjustingBrushCursor(true)
				}}
				onPointerLeave={() => {
					setShowAdjustingBrushCursor(false)
				}}
			>
				<div className="my-2 flex w-full flex-col text-xs">
					<div className="pb-1 text-xs font-medium text-base-200">
						{t("healingPanel.brushSetting")}
					</div>
					<Slider
						title={t("healingPanel.sliderBrushSize")}
						value={healingSizeToSliderValue(cursor.radius)}
						min={1}
						defaultValue={0.5}
						onUpdate={(val) => onUpdateHandler("radius", sliderValueToHealingSize(val))}
						onUpdateEnded={() => {
							/*do nothing*/
						}}
					/>
					<Slider
						title={t("healingPanel.sliderFeather")}
						value={cursor.feather}
						min={0}
						defaultValue={0.0}
						onUpdate={(val) => onUpdateHandler("feather", val)}
						onUpdateEnded={(_, displayedValue) =>
							props.onUpdateEnded("Feather", displayedValue)
						}
					/>
				</div>
			</div>

			{/* Selected Removal Section */}
			{shouldShowSelectedHealingSection && (
				<div
					className="my-2 flex w-full flex-col rounded-md bg-base-700/50 p-2 text-xs dark:bg-base-700"
					onPointerEnter={() => {
						setMouseOverSelectedHealingPanel(true)
					}}
					onPointerLeave={() => {
						setMouseOverSelectedHealingPanel(false)
					}}
				>
					<div className="flex flex-row items-center justify-between pb-1">
						<div
							className={clsx(
								"text-xs font-medium",
								isPersistedSelectedHealing ? "text-base-200" : "text-base-400",
							)}
						>
							{t("healingPanel.selectedRemoval")}
						</div>
						<button
							className={clsx(
								"m-1 text-base-400",
								!isAiPending && "hover:text-base-100 dark:hover:text-base-200",
							)}
							onClick={deleteSelectedHealing}
							aria-label={t("healingPanel.ariaDeleteHealing")}
							disabled={isAiPending}
						>
							<Icon name="delete" size={14} />
						</button>
					</div>
					<Slider
						title={t("healingPanel.sliderFeather")}
						value={
							isPersistedSelectedHealing
								? (selectedHealing.strokes[0]?.feather ?? 0.0)
								: (visibleItem?.strokes[0]?.feather ?? 0.0)
						}
						min={0}
						defaultValue={0.0}
						onUpdate={(val) => {
							setIsHealingParametersAdjusting(true)
							onUpdateSelectedHealing("feather", val)
						}}
						onUpdateEnded={(_, displayedValue) => {
							setIsHealingParametersAdjusting(false)
							props.onUpdateEnded("Feather", displayedValue)
						}}
						onUpdateCancelled={() => {
							setIsHealingParametersAdjusting(false)
						}}
						disabled={!isPersistedSelectedHealing || isAiPending}
					/>
					<Slider
						title={t("healingPanel.sliderOpacity")}
						value={
							isPersistedSelectedHealing
								? (selectedHealing.strokes[0]?.density ?? 1.0)
								: (visibleItem?.strokes[0]?.density ?? 1.0)
						}
						min={0}
						defaultValue={1.0}
						onUpdate={(val) => {
							setIsHealingParametersAdjusting(true)
							onUpdateSelectedHealing("density", val)
						}}
						onUpdateEnded={(_, displayedValue) => {
							setIsHealingParametersAdjusting(false)
							props.onUpdateEnded("Opacity", displayedValue)
						}}
						onUpdateCancelled={() => {
							setIsHealingParametersAdjusting(false)
						}}
						disabled={!isPersistedSelectedHealing || isAiPending}
					/>

					{/* Expand Mask Area Toggle */}
					{healingMode !== "healing" && (
						<div className="flex flex-row items-center space-x-4 py-1 pr-1 text-base-400">
							{t("healingPanel.expandMaskArea")}
							<div className="flex-1"></div>
							<Switch
								trackingLabel="expand_mask_area"
								aria-label={t("healingPanel.ariaExpandHealingMaskArea")}
								checked={!applyBrushMask}
								disabled={isAiPending || !isPersistedSelectedHealing}
								onCheckedChange={(checked) => handleApplyBrushMaskChange(!checked)}
							/>
						</div>
					)}

					{/* Fix Edge Mismatches Toggle - Only shown in debug mode */}
					{isAIDebugEnabled() && (
						<div className="flex flex-row items-center space-x-4 py-1 pr-1 text-base-400">
							{t("healingPanel.fixEdgeMismatches")}
							<div className="flex-1"></div>
							<Switch
								trackingLabel="fix_edge_mismatches"
								aria-label={t("healingPanel.ariaFixHealingEdgeMismatches")}
								checked={applyAlignment}
								disabled={isAiPending}
								onCheckedChange={(checked) => handleApplyAlignmentChange(checked)}
							/>
						</div>
					)}
				</div>
			)}

			<div className="">
				<div className="flex">
					<Button
						disabled={adjustments.healingMasks?.length === 0}
						trackingLabel="healing-invisible"
						variant="icon"
						size="custom"
						onPointerDown={handlePointerDown}
						onPointerUp={handlePointerUp}
						onPointerLeave={handlePointerUp}
					>
						<Icon
							name={isHealingPreviewEnabled ? "healing_after" : "healing_before"}
							className="size-4"
						/>
					</Button>
					<div className="ml-auto flex">
						<Button
							disabled={adjustments.healingMasks?.length === 0}
							trackingLabel="healing-reset"
							variant="text"
							size="custom"
							className="right-2 mr-3"
							onClick={() => resetAllHealings()}
						>
							{t("common:action.reset")}
						</Button>
						<Button
							trackingLabel="healing-close"
							variant="text"
							size="custom"
							className="right-2 mr-2"
							onClick={() => setHealingActive(false)}
						>
							{t("common:action.close")}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
